import {
  useGetApiV1ExamCorrectExamAnswerid,
  useGetApiV1ExamCorrectionGridExampublishid,
} from '@/shared/api/generated';

/**
 * Hook para descobrir o `componentType` de uma questão a partir do detalhe de
 * uma resposta qualquer dela — o campo é o mesmo para qualquer aluno da mesma
 * questão, então basta uma amostra (página 1, tamanho 1 do subgrid, sem
 * filtrar por pendência) em vez de percorrer as páginas atrás de um pendente.
 *
 * Usado para manter o gatilho de correção em lote por IA desabilitado em
 * questões que não são dissertativas (`MultilineText`), sem esperar o operador
 * abrir o diálogo de confirmação para descobrir isso.
 *
 * @param examPublishId - ID da publicação do exame
 * @param examItemId - ID do item de questão
 * @param enabled - Só busca quando `true` (ex.: a questão tem respostas pendentes)
 * @returns `componentType` (`undefined` enquanto carrega ou se desabilitado) e `isLoading`
 *
 * @example
 * ```ts
 * const { componentType, isLoading } = useQuestionComponentType(examPublishId, examItemId, pendingCount > 0);
 * ```
 */
export function useQuestionComponentType(
  examPublishId: number,
  examItemId: number,
  enabled: boolean,
) {
  const sampleQuery = useGetApiV1ExamCorrectionGridExampublishid(
    examPublishId,
    { examItemId, pageNumber: 1, pageSize: 1 },
    { query: { enabled } },
  );
  const sampleAnswerId = sampleQuery.data?.data?.[0]?.id;

  const detailsQuery = useGetApiV1ExamCorrectExamAnswerid(sampleAnswerId ?? 0, {
    query: { enabled: enabled && sampleAnswerId !== undefined },
  });

  return {
    componentType: detailsQuery.data?.componentType,
    isLoading: sampleQuery.isLoading || (sampleAnswerId !== undefined && detailsQuery.isLoading),
  } as const;
}
